import _extends from "@babel/runtime/helpers/esm/extends";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import React from "react";
import loader, { PageResourceStatus } from "./loader";
import redirects from "./redirects.json";
import { apiRunner } from "./api-runner-browser";
import emitter from "./emitter";
import { RouteAnnouncerProps } from "./route-announcer-props";
import { navigate as reachNavigate } from "@reach/router";
import { globalHistory } from "@reach/router/lib/history";
import { parsePath } from "gatsby-link";

// Convert to a map for faster lookup in maybeRedirect()
import { jsx as ___EmotionJSX } from "@emotion/core";
const redirectMap = new Map();
const redirectIgnoreCaseMap = new Map();
redirects.forEach(redirect => {
  if (redirect.ignoreCase) {
    redirectIgnoreCaseMap.set(redirect.fromPath, redirect);
  } else {
    redirectMap.set(redirect.fromPath, redirect);
  }
});
function maybeRedirect(pathname) {
  let redirect = redirectMap.get(pathname);
  if (!redirect) {
    redirect = redirectIgnoreCaseMap.get(pathname.toLowerCase());
  }
  if (redirect != null) {
    if (process.env.NODE_ENV !== "production") {
      if (!loader.isPageNotFound(pathname)) {
        console.error("The route \"" + pathname + "\" matches both a page and a redirect; this is probably not intentional.");
      }
    }
    window.___replace(redirect.toPath);
    return true;
  } else {
    return false;
  }
}
const onPreRouteUpdate = (location, prevLocation) => {
  if (!maybeRedirect(location.pathname)) {
    apiRunner("onPreRouteUpdate", {
      location,
      prevLocation
    });
  }
};
const onRouteUpdate = (location, prevLocation) => {
  if (!maybeRedirect(location.pathname)) {
    apiRunner("onRouteUpdate", {
      location,
      prevLocation
    });
    if (process.env.GATSBY_EXPERIMENTAL_QUERY_ON_DEMAND && process.env.GATSBY_QUERY_ON_DEMAND_LOADING_INDICATOR === "true") {
      emitter.emit("onRouteUpdate", {
        location,
        prevLocation
      });
    }
  }
};
const navigate = function (to, options) {
  if (options === void 0) {
    options = {};
  }
  // Support forward/backward navigation with numbers
  // navigate(-2) (jumps back 2 history steps)
  // navigate(2)  (jumps forward 2 history steps)
  if (typeof to === "number") {
    globalHistory.navigate(to);
    return;
  }
  let {
    pathname
  } = parsePath(to);
  let redirect = redirectMap.get(pathname);
  if (!redirect) {
    redirect = redirectIgnoreCaseMap.get(pathname.toLowerCase());
  }

  // If we're redirecting, just replace the passed in pathname
  // to the one we want to redirect to.
  if (redirect) {
    to = redirect.toPath;
    pathname = parsePath(to).pathname;
  }

  // If we had a service worker update, no matter the path, reload window and
  // reset the pathname whitelist
  if (window.___swUpdated) {
    window.location = pathname;
    return;
  }

  // Start a timer to wait for a second before transitioning and showing a
  // loader in case resources aren't around yet.
  const timeoutId = setTimeout(() => {
    emitter.emit("onDelayedLoadPageResources", {
      pathname
    });
    apiRunner("onRouteUpdateDelayed", {
      location: window.location
    });
  }, 1000);
  loader.loadPage(pathname).then(pageResources => {
    // If no page resources, then refresh the page
    // Do this, rather than simply `window.location.reload()`, so that
    // pressing the back/forward buttons work - otherwise when pressing
    // back, the browser will just change the URL and expect JS to handle
    // the change, which won't always work since it might not be a Gatsby
    // page.
    if (!pageResources || pageResources.status === PageResourceStatus.Error) {
      window.history.replaceState({}, "", location.href);
      window.location = pathname;
      clearTimeout(timeoutId);
      return;
    }

    // If the loaded page has a different compilation hash to the
    // window, then a rebuild has occurred on the server. Reload.
    if (process.env.NODE_ENV === "production" && pageResources) {
      if (pageResources.page.webpackCompilationHash !== window.___webpackCompilationHash) {
        // Purge plugin-offline cache
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller !== null && navigator.serviceWorker.controller.state === "activated") {
          navigator.serviceWorker.controller.postMessage({
            gatsbyApi: "clearPathResources"
          });
        }
        window.location = pathname;
      }
    }
    reachNavigate(to, options);
    clearTimeout(timeoutId);
  });
};
function shouldUpdateScroll(prevRouterProps, _ref) {
  let {
    location
  } = _ref;
  const {
    pathname,
    hash
  } = location;
  const results = apiRunner("shouldUpdateScroll", {
    prevRouterProps,
    // `pathname` for backwards compatibility
    pathname,
    routerProps: {
      location
    },
    getSavedScrollPosition: args => [0, this._stateStorage.read(args, args.key)]
  });
  if (results.length > 0) {
    // Use the latest registered shouldUpdateScroll result, this allows users to override plugin's configuration
    // @see https://github.com/gatsbyjs/gatsby/issues/12038
    return results[results.length - 1];
  }
  if (prevRouterProps) {
    const {
      location: {
        pathname: oldPathname
      }
    } = prevRouterProps;
    if (oldPathname === pathname) {
      // Scroll to element if it exists, if it doesn't, or no hash is provided,
      // scroll to top.
      return hash ? decodeURI(hash.slice(1)) : [0, 0];
    }
  }
  return true;
}
function init() {
  // The "scroll-behavior" package expects the "action" to be on the location
  // object so let's copy it over.
  globalHistory.listen(args => {
    args.location.action = args.action;
  });
  window.___push = to => navigate(to, {
    replace: false
  });
  window.___replace = to => navigate(to, {
    replace: true
  });
  window.___navigate = (to, options) => navigate(to, options);

  // Check for initial page-load redirect
  maybeRedirect(window.location.pathname);
}
let RouteAnnouncer = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(RouteAnnouncer, _React$Component);
  function RouteAnnouncer(props) {
    var _this;
    _this = _React$Component.call(this, props) || this;
    _this.announcementRef = /*#__PURE__*/React.createRef();
    return _this;
  }
  var _proto = RouteAnnouncer.prototype;
  _proto.componentDidUpdate = function componentDidUpdate(prevProps, nextProps) {
    requestAnimationFrame(() => {
      let pageName = "new page at " + this.props.location.pathname;
      if (document.title) {
        pageName = document.title;
      }
      const pageHeadings = document.querySelectorAll("#gatsby-focus-wrapper h1");
      if (pageHeadings && pageHeadings.length) {
        pageName = pageHeadings[0].textContent;
      }
      const newAnnouncement = "Navigated to " + pageName;
      if (this.announcementRef.current) {
        const oldAnnouncement = this.announcementRef.current.innerText;
        if (oldAnnouncement !== newAnnouncement) {
          this.announcementRef.current.innerText = newAnnouncement;
        }
      }
    });
  };
  _proto.render = function render() {
    return ___EmotionJSX("div", _extends({}, RouteAnnouncerProps, {
      ref: this.announcementRef
    }));
  };
  return RouteAnnouncer;
}(React.Component);
const compareLocationProps = (prevLocation, nextLocation) => {
  var _prevLocation$state, _nextLocation$state;
  if (prevLocation.href !== nextLocation.href) {
    return true;
  }
  if ((prevLocation === null || prevLocation === void 0 ? void 0 : (_prevLocation$state = prevLocation.state) === null || _prevLocation$state === void 0 ? void 0 : _prevLocation$state.key) !== (nextLocation === null || nextLocation === void 0 ? void 0 : (_nextLocation$state = nextLocation.state) === null || _nextLocation$state === void 0 ? void 0 : _nextLocation$state.key)) {
    return true;
  }
  return false;
};

// Fire on(Pre)RouteUpdate APIs
let RouteUpdates = /*#__PURE__*/function (_React$Component2) {
  _inheritsLoose(RouteUpdates, _React$Component2);
  function RouteUpdates(props) {
    var _this2;
    _this2 = _React$Component2.call(this, props) || this;
    onPreRouteUpdate(props.location, null);
    return _this2;
  }
  var _proto2 = RouteUpdates.prototype;
  _proto2.componentDidMount = function componentDidMount() {
    onRouteUpdate(this.props.location, null);
  };
  _proto2.shouldComponentUpdate = function shouldComponentUpdate(prevProps) {
    if (compareLocationProps(prevProps.location, this.props.location)) {
      onPreRouteUpdate(this.props.location, prevProps.location);
      return true;
    }
    return false;
  };
  _proto2.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (compareLocationProps(prevProps.location, this.props.location)) {
      onRouteUpdate(this.props.location, prevProps.location);
    }
  };
  _proto2.render = function render() {
    return ___EmotionJSX(React.Fragment, null, this.props.children, ___EmotionJSX(RouteAnnouncer, {
      location: location
    }));
  };
  return RouteUpdates;
}(React.Component);
export { init, shouldUpdateScroll, RouteUpdates };